import { useEffect, useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useAppVersion } from '../hooks/useAppVersion';
import { isMobileDevice } from '../lib/device';

interface PWAManagerProps {
  onReady?: () => void;
}

export function PWAManager({ onReady }: PWAManagerProps) {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const { hasUpdate, isChecking, checkForUpdates, applyUpdate, currentVersion, latestVersion } = useAppVersion();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const isMobile = isMobileDevice();

  // Check for updates on mount
  useEffect(() => {
    checkForUpdates();
  }, []);

  // Handle version check results
  useEffect(() => {
    console.log('[PWA] [DIAGNOSTIC] Version check effect triggered:', {
      isChecking,
      hasUpdate,
      currentVersion: currentVersion?.version,
      latestVersion: latestVersion?.version
    });

    if (!isChecking && hasUpdate) {
      // Check if we've already attempted an update this session
      const updateAttempted = sessionStorage.getItem('update_attempted');
      console.log('[PWA] [DIAGNOSTIC] Update available, updateAttempted:', updateAttempted);

      if (!updateAttempted) {
        console.log('[PWA] Update available - attempting automatic update');
        sessionStorage.setItem('update_attempted', 'true');
        // Automatically apply update (preferred method)
        handleAutoUpdate();
      } else {
        // Already attempted update, show banner and let app load
        console.log('[PWA] Update already attempted, showing banner and allowing app to load');
        setShowUpdateBanner(true);
        handleReady(); // Let the app load underneath the banner
      }
    } else if (!isChecking && !hasUpdate) {
      // No update needed, proceed to app
      console.log('[PWA] [DIAGNOSTIC] No update needed, calling handleReady');
      handleReady();
    } else {
      console.log('[PWA] [DIAGNOSTIC] Still checking or waiting for update info');
    }
  }, [isChecking, hasUpdate]);

  // Show install prompt for mobile users without PWA
  useEffect(() => {
    if (isMobile && !isInstalled && isInstallable && !hasUpdate) {
      // Only show install prompt if there's no update in progress
      setShowInstallPrompt(true);
    }
  }, [isMobile, isInstalled, isInstallable, hasUpdate]);

  const handleAutoUpdate = async () => {
    console.log('[PWA] [DIAGNOSTIC] handleAutoUpdate() called');
    setIsUpdating(true);
    try {
      // Small delay to ensure service worker is ready
      console.log('[PWA] [DIAGNOSTIC] Waiting 1 second before applying update');
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('[PWA] Applying update automatically');
      applyUpdate(); // This will reload the page
    } catch (error) {
      console.error('[PWA] Auto-update failed, showing banner:', error);
      setIsUpdating(false);
      setShowUpdateBanner(true);
    }
  };

  const handleManualUpdate = () => {
    console.log('[PWA] [DIAGNOSTIC] User requested manual update');
    setIsUpdating(true);
    applyUpdate();
  };

  const handleInstall = async () => {
    console.log('[PWA] [DIAGNOSTIC] handleInstall() called');
    const installed = await promptInstall();
    if (installed) {
      setShowInstallPrompt(false);
      handleReady();
    }
  };

  const handleSkipInstall = () => {
    console.log('[PWA] [DIAGNOSTIC] handleSkipInstall() called');
    setShowInstallPrompt(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
    handleReady();
  };

  const handleReady = () => {
    console.log('[PWA] [DIAGNOSTIC] handleReady() called');
    // Check if we should show install prompt one more time
    if (isMobile && !isInstalled && isInstallable) {
      const dismissed = localStorage.getItem('pwa_install_dismissed');
      console.log('[PWA] [DIAGNOSTIC] Mobile check - dismissed:', dismissed);
      if (!dismissed) {
        console.log('[PWA] [DIAGNOSTIC] Showing install prompt');
        setShowInstallPrompt(true);
        return;
      }
    }

    // All checks passed, notify parent
    console.log('[PWA] [DIAGNOSTIC] All checks passed, calling onReady()');
    if (onReady) {
      onReady();
    }
  };

  // Loading state while checking/updating
  if (isChecking || isUpdating) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center z-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-foreground">
            {isChecking ? 'Checking for updates...' : 'Updating app...'}
          </p>
          {currentVersion && latestVersion && (
            <p className="text-sm text-muted-foreground mt-2">
              v{currentVersion.version} → v{latestVersion.version}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render update banner and install prompt as overlays, not blockers
  return (
    <>
      {/* Update banner (overlay, doesn't block app) */}
      {showUpdateBanner && (
        <div className="fixed top-0 left-0 right-0 bg-primary text-primary-foreground p-4 z-50 shadow-lg">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="font-semibold">Update Available!</p>
              <p className="text-sm opacity-90">
                Version {latestVersion?.version} is ready to install
              </p>
            </div>
            <button
              onClick={handleManualUpdate}
              className="px-4 py-2 bg-white text-primary rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Update Now
            </button>
          </div>
        </div>
      )}

      {/* Install prompt for mobile users (modal, blocks app) */}
      {showInstallPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Install Trapp</h2>
              <p className="text-muted-foreground">
                Add Trapp to your home screen for the best offline experience and quick access
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleInstall}
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Add to Home Screen
              </button>
              <button
                onClick={handleSkipInstall}
                className="w-full px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors"
              >
                Continue in Browser
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
